import React, { useState, useEffect, useCallback, useRef } from "react";
import { createPortal } from "react-dom";
import { Link } from "react-router-dom";
import { adminApi } from "../../utils/adminApi";
import { paths } from "../../paths";
import { getCache, setCache, isFresh, invalidate } from "./adminCache";
import {
  fmtPts,
  timeAgo,
  fmtDate,
  toDateInput,
  tierFor,
  TIER_CLS,
  TASK_TYPES,
  STATUS_OPTIONS,
  ANNOUNCEMENT_TAGS,
  TAG_CLS,
  inputCls,
  labelCls,
  primaryBtn,
  StatCard,
  Panel,
  Modal,
  ResultModal,
  Chip,
  Toggle,
  AdminShell,
  Loading,
} from "./adminUi";

const dashStatusLabel = (k) =>
  STATUS_OPTIONS.find((s) => s.key === k)?.label || "Active";

// Chip text for a saved task on the result popup (e.g. "Active · +50 pts · Due Jun 25").
const dashTaskChip = (form) => {
  const parts = [
    dashStatusLabel(form.status),
    `+${Number(form.points) || 0} pts`,
  ];
  if (form.deadline) parts.push(`Due ${fmtDate(form.deadline)}`);
  return parts.join(" · ");
};

const AdminDashboard = () => {
  const cached = getCache("dashboard");
  const [stats, setStats] = useState(cached?.stats ?? null);
  const [cas, setCas] = useState(cached?.cas ?? []);
  const [submissions, setSubmissions] = useState(cached?.submissions ?? []);
  const [tasks, setTasks] = useState(cached?.tasks ?? []);
  const [announcements, setAnnouncements] = useState(
    cached?.announcements ?? [],
  );
  const [search, setSearch] = useState("");
  const [searchResults, setSearchResults] = useState(null); // null = no active search filter
  const [loading, setLoading] = useState(!cached);
  const [error, setError] = useState(null);

  const [reviewIndex, setReviewIndex] = useState(null); // index into pendingSubs
  const [reviewBusy, setReviewBusy] = useState(false);
  const [showAddTask, setShowAddTask] = useState(false);
  const [showPost, setShowPost] = useState(false);
  const [editingTask, setEditingTask] = useState(null);
  const [result, setResult] = useState(null); // confirmation popup after an action

  // Bumped on every new search intent (typing or clearing) so a slow response
  // for a since-superseded query can't land late and overwrite fresher state.
  const searchTokenRef = useRef(0);

  // `cas` is always the full, unfiltered roster — it feeds rank/points totals
  // that must stay stable regardless of what's typed into the search box.
  // The search box only ever affects `searchResults` (the table's row list).
  const fetchAll = useCallback(async (q = "") => {
    setLoading(true);
    setError(null);
    try {
      const [statsRes, casRes, subsRes, tasksRes, annRes] = await Promise.all([
        adminApi.get("/stats"),
        adminApi.get("/cas"),
        adminApi.get("/submissions"),
        adminApi.get("/tasks"),
        adminApi.get("/announcements"),
      ]);
      setStats(statsRes.data);
      setCas(casRes.data || []);
      setSubmissions(subsRes.data || []);
      setTasks(tasksRes.data || []);
      setAnnouncements(annRes.data || []);
      setCache("dashboard", {
        stats: statsRes.data,
        cas: casRes.data || [],
        submissions: subsRes.data || [],
        tasks: tasksRes.data || [],
        announcements: annRes.data || [],
      });
      setCache("cas", casRes.data || []);

      const token = ++searchTokenRef.current;
      if (q) {
        const searchRes = await adminApi.get("/cas", { params: { q } });
        if (searchTokenRef.current === token) setSearchResults(searchRes.data || []);
      } else {
        setSearchResults(null);
      }
    } catch (err) {
      console.error("Admin dashboard load error:", err);
      setError(
        "Unable to load admin data. Check that you're signed in as an admin.",
      );
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (isFresh("dashboard")) {
      setLoading(false);
      return;
    }
    fetchAll();
  }, [fetchAll]);

  // Debounced CA search. Clearing the box drops the filter immediately
  // (no debounce wait) so the full roster reappears right away. Each keystroke
  // invalidates any older in-flight request via the token, so a slow response
  // for text the user already deleted can't land late and reapply itself.
  useEffect(() => {
    const token = ++searchTokenRef.current;
    if (!search) {
      setSearchResults(null);
      return;
    }
    const t = setTimeout(() => {
      adminApi
        .get("/cas", { params: { q: search } })
        .then((res) => {
          if (searchTokenRef.current === token) setSearchResults(res.data || []);
        })
        .catch(console.error);
    }, 350);
    return () => clearTimeout(t);
  }, [search]);

  const handleReview = async (submissionId, action, reviewNotes, sub) => {
    setReviewBusy(true);
    try {
      await adminApi.patch(`/submissions/${submissionId}/review`, {
        action,
        reviewNotes: reviewNotes || undefined,
      });
      invalidate(
        "dashboard",
        "cas",
        "leaderboard",
        "submissions:submitted",
        "submissions:approved",
        "submissions:rejected",
        "submissions:",
      );
      setReviewIndex(null);
      await fetchAll(search);
      setResult({ kind: "review", action, sub, reason: reviewNotes });
    } catch (err) {
      alert(err.response?.data?.message || "Action failed");
    } finally {
      setReviewBusy(false);
    }
  };

  // Keep the review pager valid as the pending list shrinks after each action.
  const pendingCount = submissions.filter(
    (s) => s.status === "submitted",
  ).length;
  useEffect(() => {
    if (reviewIndex !== null && reviewIndex >= pendingCount) {
      setReviewIndex(pendingCount > 0 ? pendingCount - 1 : null);
    }
  }, [pendingCount, reviewIndex]);

  // Only show the full-screen loader on the very first load. Post-mutation
  // refetches keep the dashboard (and any open result modal) mounted instead of
  // blinking to a dark loading screen on every approve/add/edit/post.
  if (loading && !stats) {
    return <Loading label="Loading admin dashboard..." />;
  }

  if (error) {
    return (
      <div className="min-h-screen bg-[#0E0705] text-[#F8F5F2] flex flex-col items-center justify-center p-4 font-sans">
        <p className="text-red-400 mb-4 font-medium text-center">{error}</p>
        <button onClick={() => fetchAll()} className={primaryBtn}>
          Retry
        </button>
      </div>
    );
  }

  // Rank always reflects the full roster, keyed by CA id, so a search filter
  // (which only narrows which rows are shown) never reshuffles anyone's rank.
  const rankedCas = [...cas].sort((a, b) => (b.points || 0) - (a.points || 0));
  const rankById = {};
  rankedCas.forEach((ca, i) => {
    rankById[ca.id] = i + 1;
  });
  const displayCas = searchResults !== null ? searchResults : rankedCas;
  const pointsAwarded = cas.reduce((sum, c) => sum + (c.points || 0), 0);
  const pool = stats?.totalAvailablePoints || 0; // tier denominator (live task points)
  const pendingSubs = submissions.filter((s) => s.status === "submitted");
  const activeTasks = tasks.filter((t) => t.isActive).length;
  const closedTasks = tasks.length - activeTasks;

  const taskCounts = {};
  submissions.forEach((s) => {
    const id = s.Task?.id;
    if (id) taskCounts[id] = (taskCounts[id] || 0) + 1;
  });

  // Per-CA context for the review modal (points, rank, approved/done counts)
  const caById = {};
  rankedCas.forEach((ca, i) => {
    caById[ca.id] = { ...ca, rank: i + 1 };
  });
  const approvedByUser = {};
  submissions.forEach((s) => {
    const uid = s.User?.id;
    if (uid && s.status === "approved")
      approvedByUser[uid] = (approvedByUser[uid] || 0) + 1;
  });
  const reviewContext = {
    caById,
    approvedByUser,
    submissions,
    totalTasks: tasks.length,
    totalAvailablePoints: pool,
  };
  const reviewingSub = reviewIndex !== null ? pendingSubs[reviewIndex] : null;

  return (
    <AdminShell
      tab={paths.adminDashboard}
      title="Admin Dashboard"
      subtitle="Manage Campus Ambassadors, tasks, submissions, and announcements for Spirit '26."
    >
      {/* --- STAT CARDS --- */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-2 pl-2">
        <StatCard
          label="Total CA's"
          value={353}
          sub="registered ambassadors"
        />
        <StatCard
          label="Total Tasks"
          value={tasks.length}
          sub={`${activeTasks} active · ${closedTasks} closed`}
        />
        <StatCard
          label="Submissions"
          value={stats?.totalSubmissions ?? 0}
          sub={`${stats?.pendingSubmissions ?? 0} awaiting review`}
        />
        <StatCard
          label="Pts Awarded"
          value={fmtPts(pointsAwarded)}
          sub="across all CA's"
        />
      </div>

      {/* --- PANELS (equal-size 2x2 grid) --- */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-2 items-stretch">
        {/* CA MANAGEMENT */}
        <Panel
          title="CA Management"
          sub={`${cas.length} registered · search by name or college`}
          className="h-[480px]"
          action={
            <Link
              to="/admin/cas"
              className="text-xs font-bold text-[#D15B28] bg-[#D15B28]/10 hover:bg-[#D15B28]/20 transition px-4 py-2 rounded-full whitespace-nowrap flex-shrink-0"
            >
              Manage CAs →
            </Link>
          }
        >
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search by name, college, or email..."
            className="w-full bg-white border border-black/10 rounded-xl px-4 py-3 text-sm mb-3 focus:outline-none focus:border-[#D15B28] transition"
          />
          <div className="overflow-x-auto flex-1 min-h-0 overflow-y-auto">
            <table className="w-full text-left border-collapse">
              <thead className="sticky top-0 bg-[#F5EFEB] z-10">
                <tr className="text-[10px] font-extrabold tracking-wider text-[#2B1B17]/40 uppercase">
                  <th className="py-2 px-3 w-14">Rank</th>
                  <th className="py-2 px-3">Name</th>
                  <th className="py-2 px-3">College</th>
                  <th className="py-2 px-3 text-right">Points</th>
                  <th className="py-2 px-3">Tier</th>
                </tr>
              </thead>
              <tbody className="text-sm">
                {displayCas.length === 0 && (
                  <tr>
                    <td colSpan={5} className="text-center text-black/40 py-6">
                      No CAs found.
                    </td>
                  </tr>
                )}
                {displayCas.map((ca) => {
                  const tier = tierFor(ca.points || 0, pool);
                  return (
                    <tr
                      key={ca.id}
                      className="border-b border-black/[0.03] last:border-none"
                    >
                      <td className="py-2.5 px-3 font-black text-black/50">
                        #{rankById[ca.id] ?? "—"}
                      </td>
                      <td className="py-2.5 px-3 font-bold text-[#2B1B17] whitespace-nowrap">
                        {ca.name}
                      </td>
                      <td className="py-2.5 px-3 text-black/50 whitespace-nowrap">
                        {ca.college}
                      </td>
                      <td className="py-2.5 px-3 text-right font-black text-[#2B1B17]">
                        {(ca.points || 0).toLocaleString()}
                      </td>
                      <td className="py-2.5 px-3">
                        <span
                          className={`text-[9px] font-extrabold tracking-wider px-2 py-1 rounded-md ${TIER_CLS[tier]}`}
                        >
                          {tier}
                        </span>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </Panel>

        {/* PENDING SUBMISSIONS */}
        <Panel
          title="Pending Submissions"
          sub={`${pendingSubs.length} awaiting reviews`}
          className="h-[480px]"
        >
          <div className="flex flex-col divide-y divide-black/[0.05] flex-1 min-h-0 overflow-y-auto">
            {pendingSubs.length === 0 && (
              <p className="text-sm text-black/40 py-8 text-center">
                No submissions awaiting review.
              </p>
            )}
            {pendingSubs.map((s, idx) => (
              <div
                key={s.id}
                className="flex items-center justify-between gap-2 py-3"
              >
                <div className="min-w-0">
                  <p className="font-bold text-[#2B1B17] truncate">
                    {s.User?.name || "CA"}
                  </p>
                  <p className="text-xs text-black/40 truncate">
                    {s.Task?.header}
                  </p>
                </div>
                <div className="text-right flex-shrink-0">
                  <p className="text-green-600 font-bold text-sm whitespace-nowrap">
                    +{s.Task?.points ?? 0} pts
                  </p>
                  <p className="text-[11px] text-black/35">
                    {timeAgo(s.submittedAt)}
                  </p>
                </div>
                <button
                  onClick={() => setReviewIndex(idx)}
                  className="text-xs font-bold text-[#D15B28] bg-[#D15B28]/10 hover:bg-[#D15B28]/20 transition px-4 py-2 rounded-full flex-shrink-0"
                >
                  View →
                </button>
              </div>
            ))}
          </div>
        </Panel>

        {/* TASK MANAGEMENT */}
        <Panel
          title="Task Management"
          sub={`${activeTasks} Active · ${closedTasks} Closed`}
          className="h-[480px]"
          action={
            <button onClick={() => setShowAddTask(true)} className={primaryBtn}>
              + Add Task
            </button>
          }
        >
          <div className="overflow-x-auto flex-1 min-h-0 overflow-y-auto">
            <table className="w-full text-left border-collapse">
              <thead className="sticky top-0 bg-[#F5EFEB] z-10">
                <tr className="text-[10px] font-extrabold tracking-wider text-[#2B1B17]/40 uppercase">
                  <th className="py-2 px-3">Task Title</th>
                  <th className="py-2 px-3">Deadline</th>
                  <th className="py-2 px-3 text-right">Pts</th>
                  <th className="py-2 px-3 text-right">Submissions</th>
                  <th className="py-2 px-3">Status</th>
                  <th className="py-2 px-3"></th>
                </tr>
              </thead>
              <tbody className="text-sm">
                {tasks.length === 0 && (
                  <tr>
                    <td colSpan={6} className="text-center text-black/40 py-6">
                      No tasks yet.
                    </td>
                  </tr>
                )}
                {tasks.map((t) => (
                  <tr
                    key={t.id}
                    className="border-b border-black/[0.03] last:border-none"
                  >
                    <td className="py-2.5 px-3 font-bold text-[#2B1B17] whitespace-nowrap">
                      {t.header}
                    </td>
                    <td className="py-2.5 px-3 text-black/50 whitespace-nowrap">
                      {fmtDate(t.deadline)}
                    </td>
                    <td className="py-2.5 px-3 text-right font-bold text-green-600">
                      +{t.points ?? 0}
                    </td>
                    <td className="py-2.5 px-3 text-right text-black/50 whitespace-nowrap">
                      {taskCounts[t.id] || 0} / {stats?.totalCAs ?? 0}
                    </td>
                    <td className="py-2.5 px-3">
                      {(() => {
                        const st =
                          t.status || (t.isActive ? "active" : "closed");
                        const cls =
                          st === "active"
                            ? "bg-teal-100 text-teal-700"
                            : st === "draft"
                              ? "bg-amber-100 text-amber-700"
                              : "bg-gray-200 text-gray-500";
                        return (
                          <span
                            className={`text-[9px] font-extrabold tracking-wider px-2 py-1 rounded-md capitalize ${cls}`}
                          >
                            {st}
                          </span>
                        );
                      })()}
                    </td>
                    <td className="py-2.5 px-3 text-right">
                      <button
                        onClick={() => setEditingTask(t)}
                        className="text-xs font-bold text-[#D15B28] hover:opacity-80 whitespace-nowrap"
                      >
                        Edit →
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Panel>

        {/* ANNOUNCEMENTS */}
        <Panel
          title="Announcements"
          sub={`${announcements.length} ${announcements.length === 1 ? "update" : "updates"}`}
          className="h-[480px]"
          action={
            <button onClick={() => setShowPost(true)} className={primaryBtn}>
              + Post
            </button>
          }
        >
          <div className="flex flex-col gap-4 flex-1 min-h-0 overflow-y-auto">
            {announcements.length === 0 && (
              <p className="text-sm text-black/40 py-8 text-center">
                No announcements yet.
              </p>
            )}
            {announcements.map((a) => (
              <div
                key={a.id}
                className="border-b border-black/[0.05] last:border-none pb-3 last:pb-0"
              >
                <div className="flex justify-between items-start gap-2">
                  <span
                    className={`text-[9px] font-extrabold tracking-wider px-2 py-1 rounded-md uppercase ${TAG_CLS[a.tag] || TAG_CLS.Info}`}
                  >
                    {a.tag || "Info"}
                  </span>
                  <span className="text-[11px] text-black/35 whitespace-nowrap flex-shrink-0">
                    {timeAgo(a.createdAt)}
                  </span>
                </div>
                <h4 className="font-bold text-[#2B1B17] leading-snug mt-1.5">
                  {a.title}
                </h4>
                {a.body && (
                  <p className="text-xs text-black/50 mt-1 leading-relaxed">
                    {a.body}
                  </p>
                )}
              </div>
            ))}
          </div>
        </Panel>
      </div>

      {/* --- REVIEW SUBMISSION MODAL --- */}
      {reviewingSub && (
        <ReviewSubmissionModal
          sub={reviewingSub}
          index={reviewIndex}
          total={pendingSubs.length}
          ctx={reviewContext}
          busy={reviewBusy}
          onPrev={() => setReviewIndex((i) => Math.max(0, i - 1))}
          onNext={() =>
            setReviewIndex((i) => Math.min(pendingSubs.length - 1, i + 1))
          }
          onClose={() => setReviewIndex(null)}
          onReview={handleReview}
        />
      )}

      {/* --- ADD TASK MODAL --- */}
      {showAddTask && (
        <AddTaskModal
          onClose={() => setShowAddTask(false)}
          onSaved={(res) => {
            setShowAddTask(false);
            invalidate("dashboard", "tasks");
            fetchAll(search);
            setResult(res);
          }}
        />
      )}

      {/* --- POST ANNOUNCEMENT MODAL --- */}
      {showPost && (
        <PostAnnouncementModal
          totalCAs={stats?.totalCAs ?? cas.length}
          onClose={() => setShowPost(false)}
          onSaved={(res) => {
            setShowPost(false);
            invalidate("dashboard", "announcements");
            fetchAll(search);
            setResult(res);
          }}
        />
      )}

      {/* --- EDIT TASK MODAL --- */}
      {editingTask && (
        <EditTaskModal
          task={editingTask}
          onClose={() => setEditingTask(null)}
          onSaved={(res) => {
            setEditingTask(null);
            invalidate("dashboard", "tasks");
            fetchAll(search);
            setResult(res);
          }}
        />
      )}

      {/* --- RESULT / CONFIRMATION POPUPS --- */}
      {result?.kind === "created" && (
        <ResultModal
          accent="teal"
          title="Task Published!"
          message={`${result.form.header} is now live and visible to all Campus Ambassadors.`}
          chip={dashTaskChip(result.form)}
          primary={{ label: "View Task", onClick: () => setResult(null) }}
          secondary={{
            label: "Create Another",
            onClick: () => {
              setResult(null);
              setShowAddTask(true);
            },
          }}
          onClose={() => setResult(null)}
        />
      )}

      {result?.kind === "updated" && (
        <ResultModal
          accent="olive"
          title="Changes Saved!"
          message={`Your edits to ${result.form.header} have been applied immediately for all CAs.`}
          chip={dashTaskChip(result.form)}
          primary={{ label: "View Task", onClick: () => setResult(null) }}
          secondary={{ label: "Done", onClick: () => setResult(null) }}
          onClose={() => setResult(null)}
        />
      )}

      {result?.kind === "deleted" && (
        <ResultModal
          accent="red"
          danger
          title="Task Deleted"
          message={`${result.task.header} has been removed. CAs will no longer see it in their task list.`}
          primary={{
            label: "Go to Task Manager",
            onClick: () => setResult(null),
          }}
          onClose={() => setResult(null)}
        />
      )}

      {result?.kind === "announced" && (
        <ResultModal
          accent="teal"
          title="Announcement Sent!"
          message={`Your message has been broadcast to all ${result.totalCAs} CAs and is live on their dashboards.`}
          chip={`${(result.form.tag || "Info").toUpperCase()} · Sent just now`}
          primary={{
            label: "View Announcements",
            onClick: () => setResult(null),
          }}
          secondary={{
            label: "Post Another",
            onClick: () => {
              setResult(null);
              setShowPost(true);
            },
          }}
          onClose={() => setResult(null)}
        />
      )}

      {result?.kind === "review" && result.action === "approve" && (
        <ResultModal
          accent="teal"
          title="Submission Approved!"
          message={`+${result.sub?.Task?.points ?? 0} pts credited to ${result.sub?.User?.name || "this CA"}. They have been notified automatically.`}
          chip={`+${result.sub?.Task?.points ?? 0} pts · ${result.sub?.Task?.header || "Task"}`}
          primary={{ label: "Next Submission", onClick: () => setResult(null) }}
          secondary={{ label: "Done", onClick: () => setResult(null) }}
          onClose={() => setResult(null)}
        />
      )}

      {result?.kind === "review" && result.action === "reject" && (
        <ResultModal
          accent="red"
          danger
          title="Submission Rejected"
          message={`${result.sub?.User?.name || "The CA"} has been notified. No points were credited.`}
          chip={result.reason ? `Reason · ${result.reason}` : undefined}
          primary={{ label: "Done", onClick: () => setResult(null) }}
          onClose={() => setResult(null)}
        />
      )}
    </AdminShell>
  );
};

const AddTaskModal = ({ onClose, onSaved }) => {
  const [form, setForm] = useState({
    header: "",
    description: "",
    deadline: "",
    points: "",
    taskType: "Social Media",
    visibility: "all",
    requireProof: true,
    checklistSteps: "",
  });
  const [busy, setBusy] = useState(false);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const save = async () => {
    if (!form.header || !form.description) {
      alert("Task title and description are required.");
      return;
    }
    setBusy(true);
    try {
      await adminApi.post("/tasks", {
        header: form.header,
        description: form.description,
        deadline: form.deadline || null,
        points: Number(form.points) || 0,
        status: "active",
        taskType: form.taskType,
        visibility: form.visibility,
        requireProof: form.requireProof,
        checklistSteps: form.checklistSteps.trim() || null,
      });
      onSaved({ kind: "created", form: { ...form, status: "active" } });
    } catch (err) {
      alert(err.response?.data?.message || "Could not create task");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal
      label="Create Task"
      title="Add a new task for Campus Ambassadors"
      onClose={onClose}
    >
      <div className="flex flex-col gap-4">
        <div>
          <label className={labelCls}>Task Title</label>
          <input
            value={form.header}
            onChange={(e) => set("header", e.target.value)}
            className={`${inputCls} mt-1.5`}
            placeholder="e.g. Instagram Reel Post"
          />
        </div>
        <div>
          <label className={labelCls}>Description</label>
          <textarea
            value={form.description}
            onChange={(e) => set("description", e.target.value)}
            rows={3}
            className={`${inputCls} mt-1.5 resize-none`}
            placeholder="What should CAs do? Include platform, hashtags, requirements..."
          />
        </div>
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className={labelCls}>Deadline</label>
            <input
              type="date"
              value={form.deadline}
              onChange={(e) => set("deadline", e.target.value)}
              className={`${inputCls} mt-1.5`}
            />
          </div>
          <div>
            <label className={labelCls}>Points Reward</label>
            <input
              type="number"
              value={form.points}
              onChange={(e) => set("points", e.target.value)}
              className={`${inputCls} mt-1.5`}
              placeholder="e.g. 50"
            />
          </div>
        </div>
        <div>
          <label className={labelCls}>Task Type</label>
          <div className="flex flex-wrap gap-2 mt-2">
            {TASK_TYPES.map((t) => (
              <Chip
                key={t}
                active={form.taskType === t}
                onClick={() => set("taskType", t)}
              >
                {t}
              </Chip>
            ))}
          </div>
          <p className="text-xs text-black/40 mt-1.5">
            “Registration” shows CAs a referral-progress modal. Add steps below
            to turn any task into a checklist.
          </p>
        </div>
        <div>
          <label className={labelCls}>
            Checklist Steps{" "}
            <span className="text-black/30 normal-case font-medium">
              (optional · one per line)
            </span>
          </label>
          <textarea
            value={form.checklistSteps}
            onChange={(e) => set("checklistSteps", e.target.value)}
            rows={3}
            className={`${inputCls} mt-1.5 resize-none`}
            placeholder={
              "Joined the Spirit '26 WhatsApp group\nShared the registration link to your status\nTagged 3 friends in the announcement post"
            }
          />
        </div>
        <div>
          <label className={labelCls}>Visibility</label>
          <div className="flex flex-wrap gap-2 mt-2">
            <Chip
              active={form.visibility === "all"}
              onClick={() => set("visibility", "all")}
            >
              All CAs
            </Chip>
            <Chip
              active={form.visibility === "gold"}
              onClick={() => set("visibility", "gold")}
            >
              Gold Tier only
            </Chip>
          </div>
        </div>
        <div className="flex items-center justify-between border-t border-black/5 pt-4">
          <span className="font-semibold text-[#2B1B17]">
            Require proof submission
          </span>
          <Toggle
            on={form.requireProof}
            onClick={() => set("requireProof", !form.requireProof)}
          />
        </div>

        {/* Preview */}
        <div className="border-t border-black/5 pt-3">
          <p className={labelCls}>Preview — how it appears to CAs</p>
          <div className="bg-white rounded-xl p-4 border border-black/5 mt-2">
            <div className="flex justify-between items-start gap-2">
              <span className="text-[9px] font-extrabold tracking-wider px-2 py-1 rounded-md uppercase bg-teal-100 text-teal-700">
                Active
              </span>
              <span className="text-[11px] text-black/35 whitespace-nowrap">
                {form.deadline ? `Due ${fmtDate(form.deadline)}` : "No deadline"}
              </span>
            </div>
            <h4 className="font-bold text-[#2B1B17] mt-1.5">
              {form.header || "Task title"}
            </h4>
            {form.description && (
              <p className="text-xs text-black/50 mt-1 leading-relaxed">
                {form.description}
              </p>
            )}
            <div className="flex items-center flex-wrap gap-x-2 gap-y-1 mt-2.5">
              <span className="text-xs font-bold text-green-600">
                +{Number(form.points) || 0} pts
              </span>
              <span className="text-[10px] text-black/25">·</span>
              <span className="text-[10px] font-bold text-black/40 uppercase tracking-wide">
                {form.taskType}
              </span>
              {form.visibility === "gold" && (
                <span className="text-[9px] font-extrabold tracking-wider px-2 py-0.5 rounded-md uppercase bg-amber-100 text-amber-700">
                  Gold Tier only
                </span>
              )}
            </div>
          </div>
        </div>

        <div className="flex items-center gap-3 mt-1">
          <button
            onClick={save}
            disabled={busy}
            className="flex-1 bg-[#D15B28] text-white font-semibold py-3.5 rounded-full hover:bg-[#b04a1f] transition disabled:opacity-60"
          >
            {busy ? "Creating..." : "Create Task ➜"}
          </button>
          <button
            onClick={onClose}
            className="px-7 py-3.5 rounded-full bg-black/5 hover:bg-black/10 transition font-semibold text-[#2B1B17]"
          >
            Cancel
          </button>
        </div>
      </div>
    </Modal>
  );
};

const EditTaskModal = ({ task, onClose, onSaved }) => {
  const [form, setForm] = useState({
    header: task.header || "",
    description: task.description || "",
    deadline: toDateInput(task.deadline),
    points: task.points ?? "",
    status: task.status || (task.isActive ? "active" : "closed"),
    taskType: task.taskType || "Social Media",
    visibility: task.visibility || "all",
    requireProof: task.requireProof !== false,
    checklistSteps: task.checklistSteps || "",
  });
  const [busy, setBusy] = useState(false);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const save = async () => {
    if (!form.header || !form.description) {
      alert("Task title and description are required.");
      return;
    }
    setBusy(true);
    try {
      await adminApi.put(`/tasks/${task.id}`, {
        header: form.header,
        description: form.description,
        deadline: form.deadline || null,
        points: Number(form.points) || 0,
        status: form.status,
        taskType: form.taskType,
        visibility: form.visibility,
        requireProof: form.requireProof,
        checklistSteps: form.checklistSteps.trim() || null,
      });
      onSaved({ kind: "updated", form });
    } catch (err) {
      alert(err.response?.data?.message || "Could not update task");
    } finally {
      setBusy(false);
    }
  };

  const remove = async () => {
    if (!window.confirm(`Delete "${task.header}"? This cannot be undone.`))
      return;
    setBusy(true);
    try {
      await adminApi.delete(`/tasks/${task.id}`);
      onSaved({ kind: "deleted", task });
    } catch (err) {
      alert(err.response?.data?.message || "Could not delete task");
    } finally {
      setBusy(false);
    }
  };

  const statusBadge =
    form.status === "active"
      ? "bg-teal-100 text-teal-700"
      : form.status === "draft"
        ? "bg-amber-100 text-amber-700"
        : "bg-gray-200 text-gray-500";

  return (
    <Modal label="Edit Task" title={task.header} onClose={onClose}>
      <span
        className={`inline-block text-[10px] font-extrabold tracking-wider px-2.5 py-1 rounded-md uppercase -mt-2 mb-3 ${statusBadge}`}
      >
        {STATUS_OPTIONS.find((s) => s.key === form.status)?.label ||
          form.status}
      </span>
      <div className="flex flex-col gap-4">
        <div>
          <label className={labelCls}>Task Title</label>
          <input
            value={form.header}
            onChange={(e) => set("header", e.target.value)}
            className={`${inputCls} mt-1.5`}
          />
        </div>
        <div>
          <label className={labelCls}>Description</label>
          <textarea
            value={form.description}
            onChange={(e) => set("description", e.target.value)}
            rows={3}
            className={`${inputCls} mt-1.5 resize-none`}
          />
        </div>
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className={labelCls}>Deadline</label>
            <input
              type="date"
              value={form.deadline}
              onChange={(e) => set("deadline", e.target.value)}
              className={`${inputCls} mt-1.5`}
            />
          </div>
          <div>
            <label className={labelCls}>Points Reward</label>
            <input
              type="number"
              value={form.points}
              onChange={(e) => set("points", e.target.value)}
              className={`${inputCls} mt-1.5`}
            />
          </div>
        </div>
        <div>
          <label className={labelCls}>Status</label>
          <div className="flex flex-wrap gap-2 mt-2">
            {STATUS_OPTIONS.map((s) => (
              <Chip
                key={s.key}
                active={form.status === s.key}
                onClick={() => set("status", s.key)}
              >
                {s.label}
              </Chip>
            ))}
          </div>
        </div>
        <div>
          <label className={labelCls}>Task Type</label>
          <div className="flex flex-wrap gap-2 mt-2">
            {TASK_TYPES.map((t) => (
              <Chip
                key={t}
                active={form.taskType === t}
                onClick={() => set("taskType", t)}
              >
                {t}
              </Chip>
            ))}
          </div>
        </div>
        <div>
          <label className={labelCls}>Visibility</label>
          <div className="flex flex-wrap gap-2 mt-2">
            <Chip
              active={form.visibility === "all"}
              onClick={() => set("visibility", "all")}
            >
              All CAs
            </Chip>
            <Chip
              active={form.visibility === "gold"}
              onClick={() => set("visibility", "gold")}
            >
              Gold Tier only
            </Chip>
          </div>
        </div>
        <div>
          <label className={labelCls}>
            Checklist Steps{" "}
            <span className="text-black/30 normal-case font-medium">
              (optional · one per line)
            </span>
          </label>
          <textarea
            value={form.checklistSteps}
            onChange={(e) => set("checklistSteps", e.target.value)}
            rows={3}
            className={`${inputCls} mt-1.5 resize-none`}
            placeholder={
              "Joined the Spirit '26 WhatsApp group\nShared the registration link to your status\nTagged 3 friends in the announcement post"
            }
          />
        </div>
        <div className="flex items-center justify-between border-t border-black/5 pt-4">
          <div>
            <p className={labelCls}>Toggle Proof Requirement</p>
            <p className="font-semibold text-[#2B1B17] mt-1">
              Proof submission{" "}
              {form.requireProof
                ? "required (currently ON)"
                : "optional (currently OFF)"}
            </p>
          </div>
          <Toggle
            on={form.requireProof}
            onClick={() => set("requireProof", !form.requireProof)}
          />
        </div>
        <p className="text-xs text-black/40">
          Changes will apply to all CAs immediately.
        </p>
        <div className="flex items-center gap-3 mt-1">
          <button
            onClick={save}
            disabled={busy}
            className="flex-1 bg-[#D15B28] text-white font-semibold py-3.5 rounded-full hover:bg-[#b04a1f] transition disabled:opacity-60"
          >
            {busy ? "Saving..." : "Save Changes ➜"}
          </button>
          <button
            onClick={remove}
            disabled={busy}
            className="px-6 py-3.5 rounded-full border border-red-300 text-red-500 font-semibold hover:bg-red-50 transition"
          >
            Delete Task
          </button>
          <button
            onClick={onClose}
            className="px-6 py-3.5 rounded-full bg-black/5 hover:bg-black/10 transition font-semibold text-[#2B1B17]"
          >
            Cancel
          </button>
        </div>
      </div>
    </Modal>
  );
};

const MAX_MSG = 280;

const PostAnnouncementModal = ({ totalCAs = 0, onClose, onSaved }) => {
  const [form, setForm] = useState({
    tag: "Activation",
    title: "",
    body: "",
    pinnedUntil: "",
  });
  const [busy, setBusy] = useState(false);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const save = async () => {
    if (!form.title) {
      alert("Announcement title is required.");
      return;
    }
    setBusy(true);
    try {
      await adminApi.post("/announcements", {
        title: form.title,
        body: form.body,
        tag: form.tag,
        pinnedUntil: form.pinnedUntil || null,
      });
      onSaved({ kind: "announced", form, totalCAs });
    } catch (err) {
      alert(err.response?.data?.message || "Could not post announcement");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal
      label="Post Announcement"
      title="Broadcast a message to all CAs"
      onClose={onClose}
    >
      <div className="flex flex-col gap-4">
        <div>
          <label className={labelCls}>Tag Type</label>
          <div className="flex flex-wrap gap-2 mt-2">
            {ANNOUNCEMENT_TAGS.map((t) => {
              const active = form.tag === t;
              return (
                <button
                  key={t}
                  type="button"
                  onClick={() => set("tag", t)}
                  className={`text-xs font-extrabold tracking-wider uppercase px-3 py-1.5 rounded-full border transition ${
                    active
                      ? `${TAG_CLS[t]} border-transparent`
                      : "bg-transparent text-black/40 border-black/15 hover:border-black/30"
                  }`}
                >
                  {t}
                </button>
              );
            })}
          </div>
        </div>

        <div>
          <label className={labelCls}>Announcement Title</label>
          <input
            value={form.title}
            onChange={(e) => set("title", e.target.value)}
            className={`${inputCls} mt-1.5`}
            placeholder="e.g. Submit your pending proofs now"
          />
        </div>

        <div>
          <label className={labelCls}>Message</label>
          <textarea
            value={form.body}
            maxLength={MAX_MSG}
            onChange={(e) => set("body", e.target.value)}
            rows={3}
            className={`${inputCls} mt-1.5 resize-none`}
            placeholder="Write your announcement here. Be clear and concise — CAs will see this on their dashboard immediately."
          />
          <p className="text-[11px] text-black/35 mt-1">
            {form.body.length} / {MAX_MSG} characters
          </p>
        </div>

        <div>
          <label className={labelCls}>
            Pin Until{" "}
            <span className="text-black/30 normal-case font-medium">
              (optional)
            </span>
          </label>
          <div className="flex items-center gap-3 mt-1.5">
            <input
              type="date"
              value={form.pinnedUntil}
              onChange={(e) => set("pinnedUntil", e.target.value)}
              className={`${inputCls} flex-1`}
            />
            <span className="text-xs text-black/35">
              Leave blank to show indefinitely
            </span>
          </div>
        </div>

        {/* Preview */}
        <div className="border-t border-black/5 pt-3">
          <p className={labelCls}>Preview — how it appears on CA dashboard</p>
          <div className="bg-white rounded-xl p-4 border border-black/5 mt-2">
            <div className="flex justify-between items-start gap-2">
              <span
                className={`text-[9px] font-extrabold tracking-wider px-2 py-1 rounded-md uppercase ${TAG_CLS[form.tag]}`}
              >
                {form.tag}
              </span>
              <span className="text-[11px] text-black/35">just now</span>
            </div>
            <h4 className="font-bold text-[#2B1B17] mt-1.5">
              {form.title || "Announcement title"}
            </h4>
            {form.body && (
              <p className="text-xs text-black/50 mt-1 leading-relaxed">
                {form.body}
              </p>
            )}
          </div>
        </div>

        <p className="text-xs text-black/40">
          Will be sent instantly to all {totalCAs} CAs.
        </p>

        <div className="flex items-center gap-3">
          <button
            onClick={save}
            disabled={busy}
            className="flex-1 bg-[#D15B28] text-white font-semibold py-3.5 rounded-full hover:bg-[#b04a1f] transition disabled:opacity-60"
          >
            {busy ? "Posting..." : "Post Announcement ➜"}
          </button>
          <button
            onClick={onClose}
            className="px-7 py-3.5 rounded-full bg-black/5 hover:bg-black/10 transition font-semibold text-[#2B1B17]"
          >
            Cancel
          </button>
        </div>
      </div>
    </Modal>
  );
};

// Rendered via a portal straight into document.body (see call site below) so that
// its `fixed inset-0` always covers the full viewport — nesting it inside the
// normal component tree let an ancestor's CSS (e.g. a transform/filter used for
// a decorative background effect) turn "fixed" into "fixed relative to that
// ancestor", which is why the popup previously appeared to start partway down
// the page with the hero banner still showing above it uncovered.
const ReviewSubmissionModal = ({
  sub,
  index,
  total,
  ctx,
  busy,
  onPrev,
  onNext,
  onClose,
  onReview,
}) => {
  const [reason, setReason] = useState("");
  const uid = sub.User?.id;
  const tid = sub.Task?.id;
  const ca = ctx.caById[uid] || {};
  const approvedCount = ctx.approvedByUser[uid] || 0;
  const tier = tierFor(ca.points || 0, ctx.totalAvailablePoints);

  const history = ctx.submissions
    .filter((s) => s.User?.id === uid && s.Task?.id === tid)
    .sort(
      (a, b) =>
        new Date(a.submittedAt || a.createdAt) -
        new Date(b.submittedAt || b.createdAt),
    );

  const submittedAt = sub.submittedAt ? new Date(sub.submittedAt) : null;

  const modal = (
    <div
      className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm"
      onClick={onClose}
    >
      <div
        className="bg-[#FBF8F5] rounded-2xl shadow-2xl w-full max-w-4xl max-h-[92vh] overflow-y-auto p-6 md:p-7"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex justify-between items-start pb-4 border-b border-black/5">
          <div>
            <p className="text-[11px] font-black tracking-widest text-teal-600 uppercase">
              Review Submission
            </p>
            <h2 className="text-2xl font-black text-[#2B1B17] mt-1">
              {sub.Task?.header}
            </h2>
          </div>
          <div className="flex items-center gap-2">
            <div className="flex items-center gap-2 bg-black/5 rounded-full px-2 py-1.5">
              <button
                onClick={onPrev}
                disabled={index === 0}
                className="w-7 h-7 rounded-full hover:bg-black/10 transition disabled:opacity-30 flex items-center justify-center"
              >
                ←
              </button>
              <span className="text-xs font-bold text-black/50 whitespace-nowrap">
                {index + 1} of {total}
              </span>
              <button
                onClick={onNext}
                disabled={index >= total - 1}
                className="w-7 h-7 rounded-full hover:bg-black/10 transition disabled:opacity-30 flex items-center justify-center"
              >
                →
              </button>
            </div>
            <button
              onClick={onClose}
              className="w-9 h-9 rounded-full bg-black/5 hover:bg-black/10 transition flex items-center justify-center text-black/50"
            >
              ✕
            </button>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mt-5">
          {/* LEFT — proof */}
          <div>
            <div className="mt-4">
              <p className={labelCls}>Submitted Link</p>
              {sub.proofLink ? (
                <a
                  href={
                    sub.proofLink.startsWith("http")
                      ? sub.proofLink
                      : `https://${sub.proofLink}`
                  }
                  target="_blank"
                  rel="noreferrer"
                  className="mt-1.5 flex items-center justify-between gap-2 bg-white border border-black/5 rounded-lg px-3 py-3 hover:border-[#D15B28] transition"
                >
                  <span className="text-sm text-[#D15B28] font-semibold truncate break-all">
                    🔗 {sub.proofLink}
                  </span>
                  <span className="text-xs text-black/40 flex-shrink-0">
                    Open →
                  </span>
                </a>
              ) : (
                <p className="text-sm text-black/40 mt-1.5">
                  No link submitted.
                </p>
              )}
            </div>

            {sub.proofText && (
              <div className="mt-4">
                <p className={labelCls}>Notes from CA</p>
                <div className="bg-white border border-black/5 rounded-lg px-3 py-2.5 mt-1.5">
                  <p className="text-sm text-black/70 italic break-words">
                    {sub.proofText}
                  </p>
                </div>
              </div>
            )}

            {submittedAt && (
              <p className="text-xs text-black/40 mt-3">
                Submitted {timeAgo(sub.submittedAt)} ·{" "}
                {submittedAt.toLocaleString("default", {
                  month: "short",
                  day: "numeric",
                })}{" "}
                at{" "}
                {submittedAt.toLocaleTimeString([], {
                  hour: "2-digit",
                  minute: "2-digit",
                })}
              </p>
            )}
          </div>

          {/* RIGHT — CA + history */}
          <div className="flex flex-col gap-4">
            <div className="bg-[#2B1B17] rounded-xl p-4 text-white">
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 rounded-full bg-[#D15B28] flex items-center justify-center font-bold text-lg flex-shrink-0">
                  {sub.User?.name?.charAt(0).toUpperCase() || "U"}
                </div>
                <div className="min-w-0">
                  <h3 className="font-bold leading-tight truncate">
                    {sub.User?.name}
                  </h3>
                  <p className="text-xs text-white/60 truncate">
                    {sub.User?.college || "—"}
                  </p>
                </div>
                <div className="ml-auto flex flex-col items-end gap-1 flex-shrink-0">
                  <span className="text-[9px] font-black tracking-wider bg-white/15 px-2 py-0.5 rounded-full uppercase">
                    ✦ {tier}
                  </span>
                  <span className="text-[9px] font-bold text-teal-300">
                    {approvedCount} Approved
                  </span>
                </div>
              </div>
              <div className="grid grid-cols-3 gap-2 mt-4 bg-white/[0.06] rounded-lg p-3">
                <div>
                  <p className="text-[10px] text-white/50 uppercase tracking-wide">
                    Total Points
                  </p>
                  <p className="font-black">
                    {(ca.points ?? 0).toLocaleString()}
                  </p>
                </div>
                <div>
                  <p className="text-[10px] text-white/50 uppercase tracking-wide">
                    Tasks Done
                  </p>
                  <p className="font-black">
                    {approvedCount} / {ctx.totalTasks}
                  </p>
                </div>
                <div>
                  <p className="text-[10px] text-white/50 uppercase tracking-wide">
                    Rank
                  </p>
                  <p className="font-black">#{ca.rank ?? "—"}</p>
                </div>
              </div>
            </div>

            <div>
              <p className={labelCls}>Task</p>
              <div className="bg-white border border-black/5 rounded-lg p-3 mt-1.5">
                <p className="font-bold text-[#2B1B17]">{sub.Task?.header}</p>
                {sub.Task?.deadline && (
                  <p className="text-[11px] text-black/40 mt-0.5">
                    Due{" "}
                    {new Date(sub.Task.deadline).toLocaleDateString("default", {
                      month: "short",
                      day: "numeric",
                    })}{" "}
                    · +{sub.Task?.points ?? 0} pts
                  </p>
                )}
              </div>
            </div>

            <div>
              <p className={labelCls}>Submission History for this CA</p>
              <div className="bg-white border border-black/5 rounded-lg p-3 mt-1.5 flex flex-col gap-2">
                {history.map((h, i) => {
                  const isThis = h.id === sub.id;
                  const color =
                    h.status === "rejected"
                      ? "text-red-500"
                      : h.status === "approved"
                        ? "text-green-600"
                        : "text-[#D15B28]";
                  return (
                    <div key={h.id}>
                      <p className={`text-sm font-bold ${color}`}>
                        Attempt {i + 1} —{" "}
                        {isThis
                          ? "Pending (this)"
                          : h.status.charAt(0).toUpperCase() +
                            h.status.slice(1)}
                      </p>
                      {(h.reviewNotes || h.submittedAt) && (
                        <p className="text-[11px] text-black/40">
                          {h.submittedAt
                            ? new Date(h.submittedAt).toLocaleDateString(
                                "default",
                                { month: "short", day: "numeric" },
                              )
                            : ""}
                          {h.reviewNotes ? ` · "${h.reviewNotes}"` : ""}
                        </p>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>

            <div>
              <p className={labelCls}>
                Rejection Reason{" "}
                <span className="text-black/30 normal-case font-medium">
                  (fill only if rejecting)
                </span>
              </p>
              <input
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                placeholder="e.g. Missing hashtag, wrong account used..."
                className={`${inputCls} mt-1.5`}
              />
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="mt-6 pt-4 border-t border-black/5">
          <p className="text-xs text-black/40 mb-3">
            Approval will instantly credit +{sub.Task?.points ?? 0} pts to{" "}
            {sub.User?.name?.split(" ")[0] || "this CA"}'s account.
          </p>
          <div className="flex items-center gap-3">
            <button
              onClick={() => onReview(sub.id, "approve", undefined, sub)}
              disabled={busy}
              className="flex-1 bg-teal-500 text-white font-semibold py-3.5 rounded-full hover:bg-teal-600 transition disabled:opacity-60"
            >
              {busy ? "..." : "✓ Approve & Credit Points"}
            </button>
            <button
              onClick={() => {
                if (!reason) {
                  alert("Please add a rejection reason.");
                  return;
                }
                onReview(sub.id, "reject", reason, sub);
              }}
              disabled={busy}
              className="px-6 py-3.5 rounded-full border border-red-300 text-red-500 font-semibold hover:bg-red-50 transition disabled:opacity-60"
            >
              ✕ Reject Submission
            </button>
            <button
              onClick={onNext}
              disabled={index >= total - 1}
              className="px-6 py-3.5 rounded-full bg-black/5 hover:bg-black/10 transition font-semibold text-[#2B1B17] disabled:opacity-40"
            >
              Skip → Next
            </button>
          </div>
        </div>
      </div>
    </div>
  );

  return createPortal(modal, document.body);
};

export default AdminDashboard;
